"use client"

import { useState, useEffect } from "react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { getDurationPresets, getUnitLabel } from "@lib/util/rental"
import { convertToLocale } from "@lib/util/money"
import type { RentalUnit } from "@/types/rental"
import { useTranslations, useLocale } from "next-intl"

interface RentalDurationSelectorProps {
  rentalUnit: RentalUnit
  rentalPrice: number
  minDuration: number
  maxDuration: number | null
  selectedDuration: number
  currencyCode: string
  onDurationChange: (duration: number) => void
  disabled?: boolean
}

export default function RentalDurationSelector({
  rentalUnit,
  rentalPrice,
  minDuration,
  maxDuration,
  selectedDuration,
  currencyCode,
  onDurationChange,
  disabled = false,
}: RentalDurationSelectorProps) {
  const t = useTranslations('product.rental')
  const locale = useLocale()

  // State for custom input
  const [customValue, setCustomValue] = useState<string>('')
  const [showCustomInput, setShowCustomInput] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Get presets based on rental unit and constraints
  const presets = getDurationPresets(rentalUnit, minDuration, maxDuration)

  // Check if current selection matches any preset
  const isPresetSelected = presets.some(p => p.value === selectedDuration)

  // Update custom value when selectedDuration changes and doesn't match presets
  useEffect(() => {
    if (!isPresetSelected && selectedDuration > 0) {
      setCustomValue(selectedDuration.toString())
      setShowCustomInput(true)
    }
  }, [selectedDuration, isPresetSelected])

  // Handle preset click
  const handlePresetClick = (value: number) => {
    setError(null)
    setShowCustomInput(false)
    setCustomValue('')
    onDurationChange(value)
  }

  // Handle custom input change
  const handleCustomChange = (value: string) => {
    setCustomValue(value)

    if (!value) {
      setError(null)
      return
    }

    const numValue = parseInt(value, 10)

    if (isNaN(numValue) || numValue < 1) {
      setError(t('errors.invalidDuration'))
      return
    }

    if (numValue < minDuration) {
      setError(t('minDuration', { min: minDuration, unit: getUnitLabel(rentalUnit, minDuration, locale) }))
      return
    }

    if (maxDuration !== null && numValue > maxDuration) {
      setError(t('maxDuration', { max: maxDuration, unit: getUnitLabel(rentalUnit, maxDuration, locale) }))
      return
    }

    setError(null)
    onDurationChange(numValue)
  }

  // Handle "Other" button click
  const handleOtherClick = () => {
    setShowCustomInput(true)
    setCustomValue(selectedDuration.toString())
  }

  // Get unit abbreviation for presets
  const getUnitAbbrev = (unit: RentalUnit): string => {
    const abbrevs: Record<string, Record<RentalUnit, string>> = {
      ru: { day: 'д', week: 'нед', month: 'мес' },
      en: { day: 'd', week: 'w', month: 'mo' },
      cs: { day: 'd', week: 't', month: 'm' },
      uk: { day: 'д', week: 'тиж', month: 'міс' },
    }
    return abbrevs[locale]?.[unit] || abbrevs['en'][unit]
  }

  // Format price per period
  const pricePerPeriod = convertToLocale({
    amount: rentalPrice,
    currency_code: currencyCode,
    locale: locale === 'ru' ? 'ru-RU' : locale === 'cs' ? 'cs-CZ' : locale === 'uk' ? 'uk-UA' : 'en-US',
  })

  const unitLabel = getUnitLabel(rentalUnit, 1, locale)

  return (
    <div className="w-full space-y-3">
      {/* Label with price info */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground-secondary">
          {t('duration')}
        </span>
        <span className="text-sm text-foreground-tertiary">
          {pricePerPeriod}/{unitLabel}
        </span>
      </div>

      {/* Preset buttons + Custom input */}
      <div className="flex flex-wrap gap-2 items-center">
        {presets.map((preset) => (
          <Button
            key={preset.value}
            type="button"
            variant={selectedDuration === preset.value && !showCustomInput ? "primary" : "outline"}
            size="sm"
            onClick={() => handlePresetClick(preset.value)}
            disabled={disabled}
            className="min-w-[60px]"
          >
            {preset.label} {getUnitAbbrev(preset.unit)}
          </Button>
        ))}

        {/* "Other" button or custom input */}
        {!showCustomInput ? (
          <Button
            type="button"
            variant={!isPresetSelected ? "primary" : "outline"}
            size="sm"
            onClick={handleOtherClick}
            disabled={disabled}
          >
            {t('customDuration')}
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={minDuration}
              max={maxDuration || undefined}
              value={customValue}
              onChange={(e) => handleCustomChange(e.target.value)}
              disabled={disabled}
              className={cn(
                "w-20 h-8 text-center",
                error && "border-destructive focus-visible:ring-destructive"
              )}
              autoFocus
            />
            <span className="text-sm text-foreground-secondary">
              {getUnitLabel(rentalUnit, parseInt(customValue) || 1, locale)}
            </span>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p className="text-xs text-destructive">{error}</p>
      )}

      {/* Min/Max hint */}
      <p className="text-xs text-foreground-tertiary">
        {t('minDuration', { min: minDuration, unit: getUnitLabel(rentalUnit, minDuration, locale) })}
        {maxDuration !== null && ` • ${t('maxDuration', { max: maxDuration, unit: getUnitLabel(rentalUnit, maxDuration, locale) })}`}
      </p>
    </div>
  )
}
